import { FC, useMemo } from 'react';
import { Plus, TrashSimple } from '@phosphor-icons/react';
import {
  IBlockBase,
  IBlockTrack,
  ICoachSessionAthlete,
  IDidacticClassification,
} from '../../../endpoints/coachSessions';
import { ISportTaxonomy } from '../../../models/trainingStatistics';
import { BlockBase } from './BlockBase';
import BlockClassification from '../../../components/ProgrammingPage/BlockClassification/BlockClassification';
import styles from './BlockBase.module.scss';
import trackStyles from './TrackBlock.module.scss';

interface ParallelBlockProps {
  block: IBlockBase;
  index: number;
  onChange: (patch: Partial<IBlockBase>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  sport?: string | null;
  taxonomy?: ISportTaxonomy | null;
  totalBlocks?: number;
  rosterAthletes?: ICoachSessionAthlete[];
}

const MAX_TRACKS = 4;

const DEFAULT_TRACK: IBlockTrack = {
  name: '',
  exerciseName: '',
  assignedAthleteIds: [],
};

const WORK_OWNER_LABELS: Record<string, string> = {
  coach: 'Coach',
  athletic_trainer: 'Preparatore',
  goalkeeping_coach: 'Coach portieri',
};

/**
 * Tipo 2 — Parallelo.
 * Il gruppo si divide in N track che lavorano contemporaneamente.
 * Spec §4.2.
 */
export const ParallelBlock: FC<ParallelBlockProps> = ({
  block,
  index,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  sport,
  taxonomy,
  totalBlocks,
  rosterAthletes,
}) => {
  const athleteNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of rosterAthletes ?? []) {
      const full = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
      if (a.athleteId && full) map.set(a.athleteId, full);
    }
    return map;
  }, [rosterAthletes]);

  const formatAthlete = (id: string) =>
    athleteNameById.get(id) ?? `…${id.slice(-6)}`;

  const tracks: IBlockTrack[] = block.tracks ?? [
    { ...DEFAULT_TRACK, name: 'Gruppo A' },
    { ...DEFAULT_TRACK, name: 'Gruppo B' },
  ];

  const updateTrack = (trackIdx: number, patch: Partial<IBlockTrack>) => {
    const updated = tracks.map((t, i) =>
      i === trackIdx ? { ...t, ...patch } : t,
    );
    onChange({ tracks: updated });
  };

  const addTrack = () => {
    if (tracks.length >= MAX_TRACKS) return;
    onChange({
      tracks: [
        ...tracks,
        { ...DEFAULT_TRACK, name: `Gruppo ${String.fromCharCode(65 + tracks.length)}` },
      ],
    });
  };

  const removeTrack = (trackIdx: number) => {
    if (tracks.length <= 2) return;
    onChange({ tracks: tracks.filter((_, i) => i !== trackIdx) });
  };

  /**
   * Bug 6a01eeeb — D&D atleti dal RosterPanel verso le drop zone dei track.
   * Rimuove l'athleteId da TUTTI gli altri track (no overlap) e lo aggiunge
   * al track destinazione.
   */
  const handleDropAthlete = (
    e: React.DragEvent<HTMLDivElement>,
    targetIdx: number,
  ) => {
    e.preventDefault();
    const athleteId = e.dataTransfer.getData('athleteId');
    if (!athleteId) return;
    const next = tracks.map((t, i) => {
      const current = (t.assignedAthleteIds ?? []).filter((id) => id !== athleteId);
      if (i === targetIdx && !current.includes(athleteId)) {
        return { ...t, assignedAthleteIds: [...current, athleteId] };
      }
      return { ...t, assignedAthleteIds: current };
    });
    onChange({ tracks: next });
  };

  const removeAthleteFromTrack = (tIdx: number, athleteId: string) => {
    const next = tracks.map((t, i) =>
      i === tIdx
        ? { ...t, assignedAthleteIds: (t.assignedAthleteIds ?? []).filter((id) => id !== athleteId) }
        : t,
    );
    onChange({ tracks: next });
  };

  return (
    <BlockBase
      index={index}
      blockType="parallel"
      title={block.exerciseName ?? `${tracks.length} gruppi paralleli`}
      durationMinutes={block.durationMinutes}
      transitionAfterMinutes={block.transitionAfterMinutes}
      onTransitionChange={(v) => onChange({ transitionAfterMinutes: v })}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {/* Duration */}
      <div className={styles.fieldGrid} style={{ marginBottom: 12 }}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Durata (min)</label>
          <input
            className={styles.fieldInput}
            type="number"
            min={1}
            value={block.durationMinutes ?? ''}
            onChange={(e) =>
              onChange({ durationMinutes: Number(e.target.value) || undefined })
            }
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>EWT (min)</label>
          <input
            className={styles.fieldInput}
            type="number"
            min={0}
            value={block.ewt ?? ''}
            onChange={(e) =>
              onChange({ ewt: Number(e.target.value) || undefined })
            }
          />
        </div>
      </div>

      {/* Track list */}
      <div className={trackStyles.trackList}>
        {tracks.map((track, tIdx) => (
          <div key={tIdx} className={trackStyles.trackCard}>
            <div className={trackStyles.trackHeader}>
              <input
                className={trackStyles.trackNameInput}
                value={track.name}
                onChange={(e) => updateTrack(tIdx, { name: e.target.value })}
                placeholder={`Gruppo ${String.fromCharCode(65 + tIdx)}`}
              />
              <select
                className={trackStyles.trackOwnerSelect}
                value={track.workOwner ?? 'coach'}
                onChange={(e) =>
                  updateTrack(tIdx, { workOwner: e.target.value as any })
                }
              >
                {Object.entries(WORK_OWNER_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              {tracks.length > 2 && (
                <button
                  type="button"
                  className={trackStyles.removeTrackBtn}
                  onClick={() => removeTrack(tIdx)}
                  title="Rimuovi gruppo"
                >
                  <TrashSimple size={13} />
                </button>
              )}
            </div>
            <input
              className={trackStyles.trackExerciseInput}
              value={track.exerciseName ?? ''}
              onChange={(e) =>
                updateTrack(tIdx, { exerciseName: e.target.value })
              }
              placeholder="Esercitazione specifica…"
            />
            <div
              className={trackStyles.trackDropZone}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => handleDropAthlete(e, tIdx)}
            >
              {(track.assignedAthleteIds ?? []).length === 0 ? (
                <span className={trackStyles.trackDropLabel}>
                  Trascina atleti qui
                </span>
              ) : (
                <div className={trackStyles.trackAthleteChips}>
                  {(track.assignedAthleteIds ?? []).map((aid) => (
                    <span
                      key={aid}
                      className={trackStyles.trackAthleteChip}
                      onClick={() => removeAthleteFromTrack(tIdx, aid)}
                      title="Rimuovi"
                    >
                      {formatAthlete(aid)} ×
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {tracks.length < MAX_TRACKS && (
          <button
            type="button"
            className={trackStyles.addTrackBtn}
            onClick={addTrack}
          >
            <Plus size={13} />
            Aggiungi gruppo
          </button>
        )}
      </div>

      {/* Notes */}
      <div className={styles.field} style={{ marginTop: 10 }}>
        <label className={styles.fieldLabel}>Note staff (private)</label>
        <textarea
          className={styles.fieldTextarea}
          value={block.staffNotes ?? ''}
          onChange={(e) => onChange({ staffNotes: e.target.value })}
          placeholder="Note visibili solo allo staff…"
        />
      </div>

      {sport && taxonomy && (
        <BlockClassification
          value={block.didacticClassification ?? {}}
          onChange={(c: IDidacticClassification) =>
            onChange({ didacticClassification: c })
          }
          taxonomy={taxonomy}
          sport={sport}
          blockData={{
            exerciseName: block.exerciseName,
            notes: block.notes,
            order: block.order,
            durationMinutes: block.durationMinutes,
          }}
          totalBlocks={totalBlocks}
        />
      )}
    </BlockBase>
  );
};
